/**
 * Curated food photo registry.
 *
 * Per design intent (chat log + BRD §3.4): real food photos are used
 * SPARINGLY — only as blurred / hero / accent imagery. Everywhere else
 * the design uses StripePlaceholder (warm tone-on-tone).
 *
 * URLs target Unsplash CDN by default with size + format hints so the
 * device only downloads what it needs.  When
 * `VITE_SUPABASE_STORAGE_PHOTOS_BUCKET` is set the registry serves from
 * Supabase Storage instead.
 *
 * This module is a backward-compatible re-export of `photo-assets.ts`.
 * Existing imports of `PHOTOS` and `PhotoKey` continue to work unchanged.
 */

import { PHOTO_MAP, getPhotoUrl } from "./photo-assets";

/**
 * Map of photo keys to their CDN URLs (Unsplash fallback — same values
 * as before).  Consumers that only need a static URL keep working.
 *
 * Under the hood this is derived from `PHOTO_MAP` so the same cache of
 * `storagePath` values is available for `getPhotoUrl`.
 *
 * NOTE: we define each key explicitly so TypeScript infers the literal
 * union `"bowls" | "vegMarket" | "ingredients" | ...` for `PhotoKey`,
 * matching the old behaviour exactly.
 */
const _buildPhotos = () =>
  Object.fromEntries(
    Object.entries(PHOTO_MAP).map(([key, asset]) => [key, asset.unsplash]),
  ) as { [K in keyof typeof PHOTO_MAP]: string };

export const PHOTOS: { [K in keyof typeof PHOTO_MAP]: string } = _buildPhotos();

export type PhotoKey = keyof typeof PHOTOS;

export { getPhotoUrl, PHOTO_MAP };
export type { PhotoAsset } from "./photo-assets";
